import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, notification } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { z } from 'zod';

import Input from '../../../Template/layoutComponents/Input';
import Select from '../../../Template/layoutComponents/SingleSelect';
import Checkbox from '../../../Template/layoutComponents/Checkmark';
import {
  getInstrumentCategoryById,
  createInstrumentCategory,
  updateInstrumentCategory,
} from './services/instrumentCategory.service.ts';

// Schema Zod
const readingPropertySchema = z.object({
  name: z.string().min(1, 'Nome da propriedade de leitura é obrigatório'),
  units: z
    .array(z.string().min(1, 'Nome da unidade é obrigatório'))
    .min(1, 'Pelo menos uma unidade é obrigatória'),
});

const propertySchema = z
  .object({
    name: z.string().min(1, 'Nome da propriedade é obrigatório'),
    type: z.enum(['string', 'number', 'enum']),
    enumValue: z.array(z.string().min(1, 'Valor do enum não pode ser vazio')).optional(),
    required: z.boolean().default(false),
  })
  .refine(
    (data) => {
      if (data.type === 'enum') {
        return data.enumValue && data.enumValue.length > 0;
      }
      return true;
    },
    {
      message: 'Valores do enum são obrigatórios quando o tipo é enum',
      path: ['enumValue'],
    },
  );

const categorySchema = z.object({
  name: z.string().min(1, 'Nome da categoria é obrigatório'),
  readingProperties: z
    .array(readingPropertySchema)
    .min(1, 'Pelo menos uma propriedade de leitura é obrigatória'),
  properties: z.array(propertySchema).min(1, 'Pelo menos uma propriedade é obrigatória'),
});

// Dados padrão
const defaultFormData = {
  name: '',
  readingProperties: [{ name: '', units: [] }],
  properties: [{ name: '', type: 'string', enumValue: [], required: false }],
};

const InstrumentCategoryForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { instance } = useMsal();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(false);
  const [unitInputs, setUnitInputs] = useState({});

  const isEditing = !!id;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(categorySchema),
    defaultValues: defaultFormData,
    mode: 'onChange',
  });

  // Field arrays
  const {
    fields: readingPropertiesFields,
    append: appendReadingProperty,
    remove: removeReadingProperty,
  } = useFieldArray({
    control,
    name: 'readingProperties',
  });

  const {
    fields: propertiesFields,
    append: appendProperty,
    remove: removeProperty,
  } = useFieldArray({
    control,
    name: 'properties',
  });

  const watchedProperties = watch('properties');

  // Funções para gerenciar unidades
  const handleUnitInputChange = (readingPropertyIndex, value) => {
    setUnitInputs((prev) => ({
      ...prev,
      [readingPropertyIndex]: value,
    }));
  };

  const addUnit = (readingPropertyIndex) => {
    const unitValue = unitInputs[readingPropertyIndex]?.trim();
    if (!unitValue) return;

    const currentUnits = watch(`readingProperties.${readingPropertyIndex}.units`) || [];
    if (!currentUnits.includes(unitValue)) {
      setValue(`readingProperties.${readingPropertyIndex}.units`, [...currentUnits, unitValue]);
    }

    // Limpar o input
    setUnitInputs((prev) => ({
      ...prev,
      [readingPropertyIndex]: '',
    }));
  };

  const removeUnit = (readingPropertyIndex, unitIndex) => {
    const currentUnits = watch(`readingProperties.${readingPropertyIndex}.units`) || [];
    const newUnits = currentUnits.filter((_, i) => i !== unitIndex);
    setValue(`readingProperties.${readingPropertyIndex}.units`, newUnits);
  };

  // Carregar dados se editando
  useEffect(() => {
    if (isEditing && id) {
      loadCategoryData();
    }
  }, [id, isEditing]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      const category = await getInstrumentCategoryById(instance, id);
      setValue('name', category.name);
      setValue('readingProperties', category.readingProperties);
      setValue('properties', category.properties);
    } catch (error) {
      console.error('Erro ao carregar categoria:', error);
      notificationApi.error({
        message: 'Erro',
        description: 'Falha ao carregar dados da categoria',
        duration: 5,
      });
      navigate('/settings/instrument-categories-v2');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditing) {
        await updateInstrumentCategory(instance, id, data);
        notificationApi.success({
          message: 'Sucesso',
          description: 'Categoria atualizada com sucesso!',
          duration: 3,
        });
      } else {
        await createInstrumentCategory(instance, data);
        notificationApi.success({
          message: 'Sucesso',
          description: 'Categoria criada com sucesso!',
          duration: 3,
        });
      }

      navigate('/settings/instrument-categories-v2');
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      notificationApi.error({
        message: 'Erro',
        description: error?.response?.data?.message || 'Falha ao salvar categoria',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    { id: 'string', label: 'Texto' },
    { id: 'number', label: 'Número' },
    { id: 'enum', label: 'Lista de Opções' },
  ];

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
      }}
    >
      {contextHolder}

      {/* Header Fixo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          background: 'white',
          borderBottom: '1px solid #f0f0f0',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/settings/instrument-categories-v2')}
            style={{ marginRight: '16px' }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
              {isEditing ? 'Editar Categoria' : 'Nova Categoria'}
            </h1>
            <p style={{ margin: '4px 0 0 0', color: '#666' }}>
              {isEditing
                ? 'Edite os dados da categoria de instrumento'
                : 'Crie uma nova categoria de instrumento'}
            </p>
          </div>
        </div>

        {/* Botões de Ação */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            type='primary'
            size='large'
            onClick={handleSubmit(onSubmit)}
            loading={loading}
            disabled={!isValid}
          >
            {isEditing ? 'Atualizar' : 'Criar'} Categoria
          </Button>
        </div>
      </div>

      {/* Área com Scroll */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px',
        }}
      >
        {/* Formulário */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '8px',
              marginBottom: '24px',
            }}
          >
            {/* Nome da Categoria */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Nome da Categoria *
              </label>
              <Controller
                name='name'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder='Digite o nome da categoria'
                    style={{ width: '100%' }}
                  />
                )}
              />
              {errors.name && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  {errors.name.message}
                </div>
              )}
            </div>

            {/* Layout em 2 Colunas */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 2px 1fr',
                gap: '24px',
                alignItems: 'start',
                width: '100%',
              }}
            >
              {/* Coluna 1: Propriedades de Leitura */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <h3 style={{ margin: 0 }}>Propriedades de Leitura *</h3>
                  <Button
                    type='dashed'
                    icon={<PlusOutlined />}
                    onClick={() => appendReadingProperty({ name: '', units: [] })}
                  >
                    Adicionar
                  </Button>
                </div>

                {readingPropertiesFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      border: '2px solid #d9d9d9',
                      borderRadius: '6px',
                      padding: '16px',
                      marginBottom: '16px',
                      background: 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Propriedade de Leitura {index + 1}</h4>
                      {readingPropertiesFields.length > 1 && (
                        <Button
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeReadingProperty(index)}
                        />
                      )}
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                        Nome *
                      </label>
                      <Controller
                        name={`readingProperties.${index}.name`}
                        control={control}
                        render={({ field }) => <Input {...field} placeholder='Ex: Temperatura' />}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                        Unidades *
                      </label>

                      {/* Input para adicionar nova unidade */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <Input
                          placeholder='Digite uma unidade (ex: °C)'
                          value={unitInputs[index] || ''}
                          onChange={(e) => handleUnitInputChange(index, e.target.value)}
                          onPressEnter={() => addUnit(index)}
                          style={{ flex: 1 }}
                        />
                        <Button
                          type='primary'
                          icon={<PlusOutlined />}
                          onClick={() => addUnit(index)}
                          disabled={!unitInputs[index]?.trim()}
                        >
                          Adicionar
                        </Button>
                      </div>

                      {/* Lista de unidades adicionadas */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {watch(`readingProperties.${index}.units`)?.length > 0 ? (
                          watch(`readingProperties.${index}.units`).map((unit, unitIndex) => (
                            <div
                              key={unitIndex}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: '#f5f5f5',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <span>{unit}</span>
                              {watch(`readingProperties.${index}.units`)?.length > 1 && (
                                <Button
                                  type='text'
                                  size='small'
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removeUnit(index, unitIndex)}
                                  style={{
                                    minWidth: 'auto',
                                    padding: '0',
                                    height: '16px',
                                    width: '16px',
                                  }}
                                />
                              )}
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              color: '#999',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              padding: '8px 0',
                            }}
                          >
                            Nenhuma unidade adicionada ainda
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divisória Visual */}
              <div
                style={{
                  width: '2px',
                  backgroundColor: '#e8e8e8',
                  minHeight: '200px',
                  alignSelf: 'stretch',
                }}
              ></div>

              {/* Coluna 2: Propriedades */}
              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <h3 style={{ margin: 0 }}>Propriedades *</h3>
                  <Button
                    type='dashed'
                    icon={<PlusOutlined />}
                    onClick={() =>
                      appendProperty({ name: '', type: 'string', enumValue: [], required: false })
                    }
                  >
                    Adicionar
                  </Button>
                </div>

                {propertiesFields.map((field, index) => (
                  <div
                    key={field.id}
                    style={{
                      border: '2px solid #d9d9d9',
                      borderRadius: '6px',
                      padding: '16px',
                      marginBottom: '16px',
                      background: 'transparent',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <h4 style={{ margin: 0 }}>Propriedade {index + 1}</h4>
                      {propertiesFields.length > 1 && (
                        <Button
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeProperty(index)}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        marginBottom: '12px',
                      }}
                    >
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                          Nome *
                        </label>
                        <Controller
                          name={`properties.${index}.name`}
                          control={control}
                          render={({ field }) => <Input {...field} placeholder='Ex: Precisão' />}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                          Tipo *
                        </label>
                        <Controller
                          name={`properties.${index}.type`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              value={{ id: field.value }}
                              options={typeOptions}
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          )}
                        />
                      </div>
                    </div>

                    {/* Valores do Enum (condicional) */}
                    {watchedProperties?.[index]?.type === 'enum' && (
                      <div style={{ marginBottom: '12px' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <label style={{ fontSize: '14px' }}>Opções do Enum *</label>
                          <Button
                            type='dashed'
                            size='small'
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const currentEnumValues =
                                watch(`properties.${index}.enumValue`) || [];
                              setValue(`properties.${index}.enumValue`, [...currentEnumValues, '']);
                            }}
                          >
                            Adicionar Opção
                          </Button>
                        </div>

                        {watch(`properties.${index}.enumValue`)?.map((enumValue, enumIndex) => (
                          <div
                            key={enumIndex}
                            style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}
                          >
                            <Controller
                              name={`properties.${index}.enumValue.${enumIndex}`}
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder='Ex: Industrial'
                                  style={{ flex: 1 }}
                                />
                              )}
                            />
                            <Button
                              type='text'
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                const currentEnumValues =
                                  watch(`properties.${index}.enumValue`) || [];
                                const newEnumValues = currentEnumValues.filter(
                                  (_, i) => i !== enumIndex,
                                );
                                setValue(`properties.${index}.enumValue`, newEnumValues);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Campo Obrigatório */}
                    <Controller
                      name={`properties.${index}.required`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          label='Campo Obrigatório'
                        />
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstrumentCategoryForm;
